/**
 * Configurações → Integrações → API do bot (spec 022, FR-004 / US3): as chaves
 * com que o fluxo publicado na Zeepchat lê e escreve no ERP. Criar (a chave
 * aparece UMA vez, para copiar), ver o último uso e revogar — a chave revogada
 * dá 401 na próxima chamada do bot. Só admin (a página inteira já é).
 */
import { useEffect, useState, type FormEvent } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Bot, Check, CircleAlert, Copy, KeyRound, Plus, TriangleAlert } from 'lucide-react';
import {
  BOT_API_KEY_NAME_MAX,
  BOT_SCOPES,
  BOT_SCOPE_HINTS,
  BOT_SCOPE_LABELS,
  type BotApiKey,
  type BotApiKeyCreated,
  type BotScope,
} from '@erp/shared';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Input, Label } from '@/components/ui/input';
import { Badge, EmptyState, Skeleton, Spinner } from '@/components/ui/misc';
import { formatDateTime } from '@/lib/utils';
import { copyText } from '@/features/vault/vault-clipboard';
import { tifluxRelativeTime as relativeTime } from './tiflux';
import { botKeyErrorMessage, botKeysOptions, useCreateBotKey, useRevokeBotKey } from './bot-keys';

export function BotApiSection() {
  const keysQ = useQuery(botKeysOptions());
  const [creating, setCreating] = useState(false);

  return (
    <section className="rounded-lg border bg-card shadow-xs">
      <div className="flex flex-wrap items-start justify-between gap-3 border-b px-4 py-3">
        <div className="min-w-0">
          <h2 className="flex items-center gap-2 text-sm font-semibold">
            <Bot className="size-4 text-primary" />
            API do bot
          </h2>
          <p className="mt-0.5 max-w-2xl text-xs text-muted-foreground">
            Chaves com que o bot do WhatsApp (fluxo publicado na Zeepchat) consulta tickets pelo telefone,
            registra notas e muda a etapa — direto no ERP. Cada chave vale só para esta empresa, com os
            escopos marcados, e pode ser revogada a qualquer momento.
          </p>
        </div>
        <Button type="button" size="sm" onClick={() => setCreating(true)}>
          <Plus />
          Criar chave
        </Button>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-muted text-left text-[13px] whitespace-nowrap">
              <th className="h-9 px-4 font-semibold">Nome</th>
              <th className="h-9 px-4 font-semibold">Escopos</th>
              <th className="h-9 px-4 font-semibold">Impressão digital</th>
              <th className="h-9 px-4 font-semibold">Criada</th>
              <th className="h-9 px-4 font-semibold">Último uso</th>
              <th className="h-9 px-4 font-semibold">Status</th>
              <th className="h-9 w-40 px-4 font-semibold">
                <span className="sr-only">Ações</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {keysQ.isPending ? (
              <tr>
                <td colSpan={7} className="px-4 py-3" aria-busy>
                  <Skeleton className="h-6 w-full" />
                </td>
              </tr>
            ) : keysQ.isError ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState
                    className="h-32"
                    message={`Erro ao carregar as chaves: ${botKeyErrorMessage(keysQ.error)}`}
                  />
                </td>
              </tr>
            ) : keysQ.data.length === 0 ? (
              <tr>
                <td colSpan={7}>
                  <EmptyState className="h-32" message="Nenhuma chave criada" />
                </td>
              </tr>
            ) : (
              keysQ.data.map((key) => <KeyRow key={key.id} apiKey={key} />)
            )}
          </tbody>
        </table>
      </div>

      <p className="border-t px-4 py-2.5 text-xs text-muted-foreground">
        A chave é mostrada uma única vez, na criação. O ERP guarda só uma impressão dela — para saber
        qual chave está no fluxo, compare os 8 primeiros caracteres do SHA-256 dela com a coluna
        “Impressão digital”.
      </p>

      <CreateKeyDialog open={creating} onClose={() => setCreating(false)} />
    </section>
  );
}

function KeyRow({ apiKey }: { apiKey: BotApiKey }) {
  const revoke = useRevokeBotKey();
  const [confirming, setConfirming] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const revoked = apiKey.revokedAt !== null;

  return (
    <>
      <tr className="border-b border-divider align-top transition-colors last:border-0 hover:bg-accent/60">
        <td className="px-4 py-2.5 font-medium">
          <span className="flex items-center gap-2">
            <KeyRound className="size-4 shrink-0 text-muted-foreground" />
            {apiKey.name}
          </span>
        </td>
        <td className="px-4 py-2.5">
          <span className="flex flex-wrap gap-1">
            {apiKey.scopes.map((s) => (
              <Badge key={s} variant="outline" title={BOT_SCOPE_HINTS[s]}>
                {BOT_SCOPE_LABELS[s]}
              </Badge>
            ))}
          </span>
        </td>
        <td className="px-4 py-2.5 font-mono text-xs text-muted-foreground">{apiKey.fingerprint}</td>
        <td className="px-4 py-2.5 whitespace-nowrap text-muted-foreground">
          {formatDateTime(apiKey.createdAt)}
          {apiKey.createdBy ? <span className="block text-xs">por {apiKey.createdBy.name}</span> : null}
        </td>
        <td className="px-4 py-2.5 whitespace-nowrap">
          {apiKey.lastUsedAt ? (
            <span title={formatDateTime(apiKey.lastUsedAt)}>{relativeTime(apiKey.lastUsedAt)}</span>
          ) : (
            <span className="text-muted-foreground">nunca usada</span>
          )}
        </td>
        <td className="px-4 py-2.5 whitespace-nowrap">
          {revoked ? (
            <span>
              <Badge variant="destructive">Revogada</Badge>
              <span className="mt-0.5 block text-xs text-muted-foreground">
                {formatDateTime(apiKey.revokedAt)}
                {apiKey.revokedBy ? ` · ${apiKey.revokedBy.name}` : ''}
              </span>
            </span>
          ) : (
            <Badge variant="success">Ativa</Badge>
          )}
        </td>
        <td className="px-4 py-2">
          {!revoked && !confirming ? (
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={() => {
                setError(null);
                setConfirming(true);
              }}
            >
              Revogar
            </Button>
          ) : null}
        </td>
      </tr>
      {confirming && !revoked ? (
        <tr className="border-b border-divider bg-[#FFF1F0]/60">
          <td colSpan={7} className="px-4 py-3">
            <div className="flex flex-wrap items-center gap-3" role="alert">
              <TriangleAlert className="size-4 shrink-0 text-destructive" />
              <p className="min-w-0 flex-1 text-sm">
                Revogar <strong>{apiKey.name}</strong>? O bot passa a receber 401 na próxima chamada com
                esta chave e o fluxo segue pelo caminho “sem ticket”. Não dá para desfazer.
              </p>
              <Button type="button" variant="outline" size="sm" onClick={() => setConfirming(false)}>
                Cancelar
              </Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                disabled={revoke.isPending}
                onClick={() =>
                  revoke.mutate(apiKey.id, {
                    onSuccess: () => setConfirming(false),
                    onError: (e) => setError(botKeyErrorMessage(e)),
                  })
                }
              >
                {revoke.isPending ? <Spinner className="size-4 border-white" /> : null}
                Revogar chave
              </Button>
            </div>
            {error ? <p className="mt-2 text-sm text-destructive">{error}</p> : null}
          </td>
        </tr>
      ) : null}
    </>
  );
}

const DEFAULT_SCOPES: BotScope[] = ['tickets:read', 'tickets:note'];

function CreateKeyDialog({ open, onClose }: { open: boolean; onClose: () => void }) {
  const create = useCreateBotKey();
  const [name, setName] = useState('');
  const [scopes, setScopes] = useState<BotScope[]>(DEFAULT_SCOPES);
  const [error, setError] = useState<string | null>(null);
  // a chave criada vive SÓ aqui, e some ao fechar
  const [created, setCreated] = useState<BotApiKeyCreated | null>(null);

  const close = () => {
    setName('');
    setScopes(DEFAULT_SCOPES);
    setError(null);
    setCreated(null);
    create.reset();
    onClose();
  };

  const submit = (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    if (!name.trim() || scopes.length === 0 || create.isPending) return;
    create.mutate(
      { name: name.trim(), scopes },
      { onSuccess: (data) => setCreated(data), onError: (err) => setError(botKeyErrorMessage(err)) }
    );
  };

  const toggle = (scope: BotScope, on: boolean) =>
    setScopes((prev) => (on ? [...new Set([...prev, scope])] : prev.filter((s) => s !== scope)));

  return (
    <Dialog open={open} onOpenChange={(o) => (!o ? close() : undefined)}>
      <DialogContent className="sm:max-w-lg">
        {created ? (
          <>
            <DialogHeader>
              <DialogTitle>Chave criada</DialogTitle>
              <DialogDescription>
                Copie agora e guarde no fluxo (variável secreta <code>erp_bot_key</code>). Ela não será
                mostrada de novo — perdeu, revogue e crie outra.
              </DialogDescription>
            </DialogHeader>
            <KeyReveal created={created} />
            <div className="flex justify-end">
              <Button type="button" onClick={close}>
                Concluído
              </Button>
            </div>
          </>
        ) : (
          <form onSubmit={submit} className="space-y-4">
            <DialogHeader>
              <DialogTitle>Nova chave da API do bot</DialogTitle>
              <DialogDescription>
                Marque só o que o fluxo usa. A etapa só muda em mesa operada no ERP; em mesa espelhada do
                TiFlux o bot recebe 409 e registra uma nota.
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-1.5">
              <Label htmlFor="bot-key-name">Nome</Label>
              <Input
                id="bot-key-name"
                value={name}
                maxLength={BOT_API_KEY_NAME_MAX}
                placeholder="Ex.: Fluxo principal — Zeepchat"
                onChange={(e) => setName(e.target.value)}
                autoFocus
              />
            </div>
            <fieldset className="space-y-2">
              <legend className="mb-1 text-sm font-medium">Escopos</legend>
              {BOT_SCOPES.map((scope) => (
                <label key={scope} className="flex cursor-pointer items-start gap-2.5">
                  <Checkbox
                    className="mt-0.5"
                    checked={scopes.includes(scope)}
                    onCheckedChange={(v) => toggle(scope, v === true)}
                    aria-label={BOT_SCOPE_LABELS[scope]}
                  />
                  <span className="text-sm leading-tight">
                    {BOT_SCOPE_LABELS[scope]}
                    <span className="ml-1.5 font-mono text-[11px] text-muted-foreground">{scope}</span>
                    <span className="block text-xs text-muted-foreground">{BOT_SCOPE_HINTS[scope]}</span>
                  </span>
                </label>
              ))}
            </fieldset>
            {error ? <p className="text-sm text-destructive">{error}</p> : null}
            <div className="flex justify-end gap-2">
              <Button type="button" variant="outline" onClick={close}>
                Cancelar
              </Button>
              <Button type="submit" disabled={!name.trim() || scopes.length === 0 || create.isPending}>
                {create.isPending ? <Spinner className="size-4 border-primary-foreground" /> : null}
                Criar chave
              </Button>
            </div>
          </form>
        )}
      </DialogContent>
    </Dialog>
  );
}

/** A chave, uma vez: campo só leitura + copiar. Nunca vai para log nem storage. */
function KeyReveal({ created }: { created: BotApiKeyCreated }) {
  const [feedback, setFeedback] = useState<'copied' | 'failed' | null>(null);

  useEffect(() => {
    if (feedback === null) return;
    const t = setTimeout(() => setFeedback(null), feedback === 'copied' ? 2000 : 4000);
    return () => clearTimeout(t);
  }, [feedback]);

  return (
    <div className="space-y-2">
      <Label htmlFor="bot-key-value">{created.apiKey.name}</Label>
      <div className="flex items-center gap-2">
        <Input
          id="bot-key-value"
          readOnly
          value={created.key}
          className="font-mono text-xs"
          onFocus={(e) => e.target.select()}
        />
        <Button
          type="button"
          variant="outline"
          size="icon-sm"
          title="Copiar a chave"
          aria-label="Copiar a chave"
          onClick={() => void copyText(created.key).then((ok) => setFeedback(ok ? 'copied' : 'failed'))}
        >
          {feedback === 'copied' ? <Check className="text-[#52C41A]" /> : <Copy />}
        </Button>
      </div>
      <p className="text-xs text-muted-foreground">
        Impressão digital <span className="font-mono">{created.apiKey.fingerprint}</span> · escopos{' '}
        {created.apiKey.scopes.map((s) => BOT_SCOPE_LABELS[s]).join(', ')}
      </p>
      {feedback === 'failed' ? (
        <p className="flex items-center gap-1 text-xs text-destructive" role="alert">
          <CircleAlert className="size-3.5 shrink-0" />
          Não foi possível copiar — selecione o valor no campo e copie manualmente.
        </p>
      ) : null}
    </div>
  );
}
